import { WorkCalendar } from "./calendar";
import { toDay } from "./date";
import type {
  Dependency,
  DependencyType,
  Project,
  Schedule,
  ScheduleDriver,
  ScheduleError,
  ScheduledTask,
  Task,
} from "./types";

export const ENGINE_VERSION = "ts-cpm/0.1.0";

export interface TreeInfo {
  order: string[];
  byId: Map<string, Task>;
  children: Map<string | null, string[]>;
  depth: Map<string, number>;
  wbs: Map<string, string>;
  leaves: Map<string, string[]>; // task → descendant leaves (self if leaf)
  ancestors: Map<string, string[]>;
}

/** Build tree metadata in document order. Orphans (missing parent) are attached to the root. */
export function buildTree(tasks: Task[]): TreeInfo {
  const byId = new Map<string, Task>();
  for (const t of tasks) byId.set(t.id, t);
  const children = new Map<string | null, string[]>();
  for (const t of tasks) {
    const pid = t.parentId && byId.has(t.parentId) ? t.parentId : null;
    const arr = children.get(pid) ?? [];
    arr.push(t.id);
    children.set(pid, arr);
  }
  const order: string[] = [];
  const depth = new Map<string, number>();
  const wbs = new Map<string, string>();
  const ancestors = new Map<string, string[]>();
  const walk = (pid: string | null, d: number, prefix: string, anc: string[]) => {
    const kids = children.get(pid) ?? [];
    kids.forEach((id, i) => {
      order.push(id);
      depth.set(id, d);
      const code = prefix ? `${prefix}.${i + 1}` : `${i + 1}`;
      wbs.set(id, code);
      ancestors.set(id, anc);
      walk(id, d + 1, code, [...anc, id]);
    });
  };
  walk(null, 0, "", []);
  const leaves = new Map<string, string[]>();
  for (let i = order.length - 1; i >= 0; i--) {
    const id = order[i]!;
    const kids = children.get(id) ?? [];
    if (kids.length === 0) leaves.set(id, [id]);
    else leaves.set(id, kids.flatMap((k) => leaves.get(k) ?? []));
  }
  return { order, byId, children, depth, wbs, leaves, ancestors };
}

interface Edge {
  dep: Dependency;
  from: string;
  to: string;
}

interface Node {
  id: string;
  task: Task;
  isSummary: boolean;
  dur: number;
  es: number;
  ef: number;
  ls: number;
  lf: number;
  lfCap: number;
  lsCap: number;
  driver: ScheduleDriver;
  constraintConflict: boolean;
  inCycle: boolean;
}

export function effectivePercent(task: Task): number {
  const p = Math.max(0, Math.min(100, task.percentComplete || 0));
  switch (task.progressMethod) {
    case "0/100":
      return p >= 100 ? 100 : 0;
    case "50/50":
      return p >= 100 ? 100 : p > 0 ? 50 : 0;
    default:
      return p;
  }
}

/** Would adding `dep` create a cycle (or link an ancestor to its own descendant)? */
export function wouldCreateCycle(project: Project, dep: Omit<Dependency, "id">): boolean {
  const tree = buildTree(project.tasks);
  if (!tree.byId.has(dep.predecessorId) || !tree.byId.has(dep.successorId)) return true;
  if (dep.predecessorId === dep.successorId) return true;
  if (tree.ancestors.get(dep.successorId)?.includes(dep.predecessorId)) return true;
  if (tree.ancestors.get(dep.predecessorId)?.includes(dep.successorId)) return true;
  const deps = [...project.dependencies, { ...dep, id: "__probe" }];
  const { cyclic } = topoSort(tree, deps);
  return cyclic.size > 0;
}

function topoSort(tree: TreeInfo, deps: Dependency[]) {
  const indeg = new Map<string, number>();
  const out = new Map<string, string[]>();
  const push = (a: string, b: string) => {
    out.get(a)!.push(b);
    indeg.set(b, (indeg.get(b) ?? 0) + 1);
  };
  for (const id of tree.order) {
    indeg.set(id, 0);
    out.set(id, []);
  }
  // structural: child → parent
  for (const id of tree.order) {
    const t = tree.byId.get(id)!;
    if (t.parentId && tree.byId.has(t.parentId)) push(id, t.parentId);
  }
  const invalid: Dependency[] = [];
  const edgesIn = new Map<string, Edge[]>();
  const edgesOut = new Map<string, Edge[]>();
  for (const id of tree.order) {
    edgesIn.set(id, []);
    edgesOut.set(id, []);
  }
  for (const dep of deps) {
    const p = dep.predecessorId;
    const s = dep.successorId;
    if (!tree.byId.has(p) || !tree.byId.has(s) || p === s) {
      invalid.push(dep);
      continue;
    }
    if (tree.ancestors.get(s)?.includes(p) || tree.ancestors.get(p)?.includes(s)) {
      invalid.push(dep);
      continue;
    }
    // successor side: expand summary successor to its leaves
    const targets = tree.leaves.get(s) ?? [s];
    for (const t of targets) {
      const e: Edge = { dep, from: p, to: t };
      edgesOut.get(p)!.push(e);
      edgesIn.get(t)!.push(e);
      push(p, t);
    }
  }
  const queue: string[] = [];
  for (const id of tree.order) if ((indeg.get(id) ?? 0) === 0) queue.push(id);
  const sorted: string[] = [];
  while (queue.length) {
    const id = queue.shift()!;
    sorted.push(id);
    for (const n of out.get(id)!) {
      const d = indeg.get(n)! - 1;
      indeg.set(n, d);
      if (d === 0) queue.push(n);
    }
  }
  const cyclic = new Set<string>();
  if (sorted.length !== tree.order.length) {
    for (const id of tree.order) if ((indeg.get(id) ?? 0) > 0) cyclic.add(id);
  }
  return { sorted, cyclic, invalid, edgesIn, edgesOut };
}

function startCandidate(type: DependencyType, lag: number, pred: { es: number; ef: number }, dur: number): number {
  switch (type) {
    case "FS":
      return pred.ef + lag;
    case "SS":
      return pred.es + lag;
    case "FF":
      return pred.ef + lag - dur;
    case "SF":
      return pred.es + lag - dur;
  }
}

export interface EarliestStart {
  /** working ordinal of the earliest start allowed by project start and predecessors (constraints ignored) */
  ordinal: number;
  /** what holds the task there */
  driver: Extract<ScheduleDriver, { kind: "project-start" | "predecessor" }>;
}

/**
 * Earliest start a leaf task could have if its own constraint were removed, given the current schedule.
 * Used by the UI to explain why a bar cannot be dragged earlier. Relations attached to ancestor summaries
 * apply to the task as well (the engine expands them the same way).
 */
export function earliestStartFor(project: Project, schedule: Schedule, taskId: string, cal: WorkCalendar): EarliestStart {
  const byId = new Map(project.tasks.map((t) => [t.id, t] as const));
  const sched = schedule.tasks.get(taskId);
  let ordinal = cal.ordinalOfISO(project.start);
  let driver: EarliestStart["driver"] = { kind: "project-start" };
  if (!sched) return { ordinal, driver };
  const targets = new Set<string>([taskId]);
  for (let p = byId.get(taskId)?.parentId; p; p = byId.get(p)?.parentId) targets.add(p);
  for (const dep of project.dependencies) {
    if (!targets.has(dep.successorId)) continue;
    const pred = schedule.tasks.get(dep.predecessorId);
    if (!pred) continue;
    const cand = startCandidate(dep.type, dep.lag, pred, sched.duration);
    if (cand > ordinal) {
      ordinal = cand;
      driver = { kind: "predecessor", dependencyId: dep.id, predecessorId: dep.predecessorId, type: dep.type, lag: dep.lag };
    }
  }
  return { ordinal, driver };
}

/** Upper bound for this node's LF given a successor's late dates. */
function finishCap(type: DependencyType, lag: number, succ: Node, dur: number): number {
  switch (type) {
    case "FS":
      return succ.ls - lag;
    case "SS":
      return succ.ls - lag + dur;
    case "FF":
      return succ.lf - lag;
    case "SF":
      return succ.lf - lag + dur;
  }
}

export function computeSchedule(project: Project, calendar?: WorkCalendar): Schedule {
  const cal = calendar ?? new WorkCalendar(project.calendar);
  const tree = buildTree(project.tasks);
  const errors: ScheduleError[] = [];
  const projectStartOrd = cal.ordinalOfISO(project.start);

  const { sorted, cyclic, invalid, edgesIn, edgesOut } = topoSort(tree, project.dependencies);
  if (cyclic.size) {
    errors.push({
      code: "cycle",
      message: "작업 연결이 서로 꼬여 있습니다(A→B→A). 꼬인 작업은 연결을 무시하고 계산했습니다.",
      taskIds: [...cyclic],
    });
  }
  for (const dep of invalid) {
    errors.push({
      code: "missing-task",
      message: "단계와 그 안의 하위 작업을 서로 연결했거나, 없는 작업을 가리키는 연결이 있어 무시했습니다.",
      taskIds: [dep.predecessorId, dep.successorId],
    });
  }

  const nodes = new Map<string, Node>();
  for (const id of tree.order) {
    const task = tree.byId.get(id)!;
    const isSummary = (tree.children.get(id)?.length ?? 0) > 0;
    const dur = isSummary ? 0 : Math.max(0, Math.round(task.duration || 0));
    if (!isSummary && (task.duration < 0 || !Number.isFinite(task.duration))) {
      errors.push({ code: "invalid-duration", message: "기간은 0일 이상이어야 합니다.", taskIds: [id] });
    }
    nodes.set(id, {
      id,
      task,
      isSummary,
      dur,
      es: projectStartOrd,
      ef: projectStartOrd,
      ls: Infinity,
      lf: Infinity,
      lfCap: Infinity,
      lsCap: Infinity,
      driver: { kind: "project-start" },
      constraintConflict: false,
      inCycle: cyclic.has(id),
    });
  }

  // Cyclic nodes are appended so they still get dates (ignoring their incoming edges).
  const forwardOrder = [...sorted, ...tree.order.filter((id) => cyclic.has(id))];

  // ---------- forward pass ----------
  for (const id of forwardOrder) {
    const n = nodes.get(id)!;
    if (n.isSummary) {
      const kids = tree.children.get(id)!.map((k) => nodes.get(k)!);
      n.es = Math.min(...kids.map((k) => k.es));
      n.ef = Math.max(...kids.map((k) => k.ef));
      n.dur = n.ef - n.es;
      n.driver = { kind: "children" };
      continue;
    }
    let es = projectStartOrd;
    let driver: ScheduleDriver = { kind: "project-start" };
    if (!n.inCycle) {
      for (const e of edgesIn.get(id)!) {
        const pred = nodes.get(e.from)!;
        const cand = startCandidate(e.dep.type, e.dep.lag, pred, n.dur);
        if (cand > es) {
          es = cand;
          driver = {
            kind: "predecessor",
            dependencyId: e.dep.id,
            predecessorId: e.from,
            type: e.dep.type,
            lag: e.dep.lag,
          };
        }
      }
    }
    const c = n.task.constraint;
    if (c) {
      const cOrd = cal.ordinalOfISO(c.date);
      if (c.type === "SNET" && cOrd > es) {
        es = cOrd;
        driver = { kind: "constraint", type: c.type, date: c.date };
      } else if (c.type === "MSO") {
        n.constraintConflict = cOrd < es;
        es = cOrd;
        driver = { kind: "constraint", type: c.type, date: c.date };
      }
    }
    n.es = es;
    n.ef = es + n.dur;
    n.driver = driver;
  }

  let finishOrd = projectStartOrd;
  for (const n of nodes.values()) if (!n.isSummary) finishOrd = Math.max(finishOrd, n.ef);

  // ---------- backward pass ----------
  const backwardOrder = [...forwardOrder].reverse();
  for (const id of backwardOrder) {
    const n = nodes.get(id)!;
    let lf = finishOrd;
    let lfCap = Infinity;
    let lsCap = Infinity;
    if (n.task.deadline) {
      // deadline is an inclusive finish date → half-open bound is ordinalBefore + 1
      const dOrd = cal.ordinalBeforeISO(n.task.deadline) + 1;
      lf = Math.min(lf, dOrd);
      lfCap = Math.min(lfCap, dOrd);
    }
    if (!n.inCycle) {
      for (const e of edgesOut.get(id)!) {
        const succ = nodes.get(e.to)!;
        if (n.isSummary) {
          if (e.dep.type === "FS" || e.dep.type === "FF") lfCap = Math.min(lfCap, finishCap(e.dep.type, e.dep.lag, succ, 0));
          else lsCap = Math.min(lsCap, e.dep.type === "SS" ? succ.ls - e.dep.lag : succ.lf - e.dep.lag);
        } else {
          lf = Math.min(lf, finishCap(e.dep.type, e.dep.lag, succ, n.dur));
        }
      }
    }
    // inherit caps from parent summary
    const parentId = n.task.parentId && tree.byId.has(n.task.parentId) ? n.task.parentId : null;
    if (parentId) {
      const p = nodes.get(parentId)!;
      lfCap = Math.min(lfCap, p.lfCap);
      if (n.es === p.es) lsCap = Math.min(lsCap, p.lsCap);
    }
    if (n.isSummary) {
      n.lfCap = lfCap;
      n.lsCap = lsCap;
      continue;
    }
    lf = Math.min(lf, lfCap);
    if (lsCap !== Infinity) lf = Math.min(lf, lsCap + n.dur);
    n.lf = lf;
    n.ls = lf - n.dur;
  }
  // summary late dates from children (children come after parents in document order)
  for (let i = tree.order.length - 1; i >= 0; i--) {
    const n = nodes.get(tree.order[i]!)!;
    if (!n.isSummary) continue;
    const kids = tree.children.get(n.id)!.map((k) => nodes.get(k)!);
    n.ls = Math.min(...kids.map((k) => k.ls));
    n.lf = Math.max(...kids.map((k) => k.lf));
  }

  // ---------- assemble ----------
  const threshold = project.criticalThreshold ?? 0;
  const tasks = new Map<string, ScheduledTask>();
  const percent = new Map<string, number>();
  const floatOf = new Map<string, number>();
  const criticalOf = new Map<string, boolean>();
  // leaves first (reverse document order gives children before parents)
  for (let i = tree.order.length - 1; i >= 0; i--) {
    const id = tree.order[i]!;
    const n = nodes.get(id)!;
    if (n.isSummary) {
      const kids = tree.children.get(id)!;
      let wsum = 0;
      let psum = 0;
      for (const k of kids) {
        const kn = nodes.get(k)!;
        const w = kn.dur;
        wsum += w;
        psum += w * (percent.get(k) ?? 0);
      }
      if (wsum === 0) {
        // all milestones → equal weights
        psum = kids.reduce((a, k) => a + (percent.get(k) ?? 0), 0);
        wsum = kids.length || 1;
      }
      percent.set(id, Math.round((psum / wsum) * 10) / 10);
      floatOf.set(id, Math.min(...kids.map((k) => floatOf.get(k) ?? 0)));
      criticalOf.set(id, kids.some((k) => criticalOf.get(k)));
    } else {
      percent.set(id, effectivePercent(n.task));
      const tf = n.ls - n.es;
      floatOf.set(id, tf);
      criticalOf.set(id, tf <= threshold && percent.get(id)! < 100);
    }
  }

  const finishDisplayOrd = (n: Node) => (n.dur > 0 ? n.ef - 1 : n.es);
  let projectFinishDisp = projectStartOrd;
  for (const id of tree.order) {
    const n = nodes.get(id)!;
    const isMilestone = !n.isSummary && n.dur === 0;
    const startISO = cal.isoOf(n.es);
    const finishISO = cal.isoOf(finishDisplayOrd(n));
    projectFinishDisp = Math.max(projectFinishDisp, finishDisplayOrd(n));
    let freeFloat = finishOrd - n.ef;
    if (!n.isSummary && !n.inCycle) {
      for (const e of edgesOut.get(id)!) {
        const s = nodes.get(e.to)!;
        const slack =
          e.dep.type === "FS"
            ? s.es - e.dep.lag - n.ef
            : e.dep.type === "SS"
              ? s.es - e.dep.lag - n.es
              : e.dep.type === "FF"
                ? s.ef - e.dep.lag - n.ef
                : s.ef - e.dep.lag - n.es;
        freeFloat = Math.min(freeFloat, slack);
      }
    }
    tasks.set(id, {
      id,
      es: n.es,
      ef: n.ef,
      ls: n.ls,
      lf: n.lf,
      start: startISO,
      finish: finishISO,
      duration: n.dur,
      totalFloat: floatOf.get(id) ?? 0,
      freeFloat: n.isSummary ? floatOf.get(id) ?? 0 : freeFloat,
      critical: criticalOf.get(id) ?? false,
      isSummary: n.isSummary,
      isMilestone,
      depth: tree.depth.get(id) ?? 0,
      wbs: tree.wbs.get(id) ?? "",
      percentComplete: percent.get(id) ?? 0,
      deadlineMissed: !!n.task.deadline && toDay(finishISO) > toDay(n.task.deadline),
      constraintConflict: n.constraintConflict,
      driver: n.driver,
    });
  }

  const criticalPath = tree.order
    .filter((id) => {
      const t = tasks.get(id)!;
      return !t.isSummary && t.critical;
    })
    .sort((a, b) => tasks.get(a)!.es - tasks.get(b)!.es);

  return {
    engineVersion: ENGINE_VERSION,
    computedAt: new Date().toISOString(),
    projectStart: cal.isoOf(projectStartOrd),
    projectFinish: cal.isoOf(projectFinishDisp),
    projectDuration: projectFinishDisp - projectStartOrd + 1,
    tasks,
    order: tree.order,
    criticalPath,
    errors,
  };
}
